import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Container,
  Button,
  HStack,
  VStack,
  useDisclosure,
  Image,
  Badge,
  Spinner,
  useColorModeValue,
  SimpleGrid,
  Icon,
  Flex
} from '@chakra-ui/react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react';
import { FiClock, FiMapPin, FiBookOpen, FiHeart, FiCalendar, FiMap, FiShare2 } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { API } from '../utils/apiBase';
import { cld } from '../utils/cloudinary';
import PageTransition from '../components/PageTransition';
import ShareCardModal from '../components/common/ShareCardModal';

// Helper functions (same as before)
function getDailyVerse(list) {
  if (!Array.isArray(list) || list.length === 0) return null;
  const hoy = new Date();
  const start = new Date(hoy.getFullYear(), 0, 0);
  const oneDay = 1000 * 60 * 60 * 24;
  const dayOfYear = Math.floor((hoy - start) / oneDay);
  const idx = dayOfYear % list.length;
  return list[idx];
}

const normalizeDate = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function normalizeEventsPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.events)) return payload.events;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

function normalizeFixedEventsPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

function normalizeVersesPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

function safeHtmlAllowSup(html = '') {
  const s = String(html);
  return s
    .replace(/<\s*sup\s*>/gi, '::OPEN_SUP::')
    .replace(/<\s*\/\s*sup\s*>/gi, '::CLOSE_SUP::')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/::OPEN_SUP::/g, '<sup>')
    .replace(/::CLOSE_SUP::/g, '</sup>');
}

function shouldRenderFixedEventOnDate(fixedEvent, date) {
  const weekday = Number(fixedEvent.weekday ?? 0);
  if (date.getDay() !== weekday) return false;

  if (fixedEvent.firstSundayOnly) {
    const dayOfMonth = date.getDate();
    return dayOfMonth <= 7;
  }
  return true;
}

const MotionBox = motion(Box);

export default function Home() {
  const boxBg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const glassBg = useColorModeValue('whiteAlpha.700', 'blackAlpha.600');

  const [daily, setDaily] = useState(null);
  const [vErr, setVErr] = useState(null);

  const [events, setEvents] = useState([]);
  const [fixedEvents, setFixedEvents] = useState([]);
  const [loadingEv, setLoadingEv] = useState(true);
  const [errEv, setErrEv] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentEvent, setCurrentEvent] = useState(null);

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const shareBtnBg = useColorModeValue('whiteAlpha.800', 'blackAlpha.600');

  useEffect(() => {
    fetch(`${API}/metrics/visits/increment`, {
      method: 'POST',
    }).catch(() => {
      if (process.env.NODE_ENV === 'development') {
        console.error('[metrics] Error al incrementar visitas');
      }
    });
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setVErr(null);
        const payload = await fetchJson(`${API}/verses?limit=1000`);
        if (!alive) return;
        const items = normalizeVersesPayload(payload);
        setDaily(getDailyVerse(items));
      } catch (e) {
        if (alive) setVErr(e?.message || 'Error al cargar versículo');
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingEv(true);
        setErrEv(null);
        const [eventsPayload, fixedPayload] = await Promise.all([
          fetchJson(`${API}/events?limit=1000`),
          fetchJson(`${API}/fixed-events`),
        ]);
        if (!alive) return;
        setEvents(normalizeEventsPayload(eventsPayload));
        setFixedEvents(normalizeFixedEventsPayload(fixedPayload));
      } catch (e) {
        if (alive) setErrEv(e?.message || 'Error al cargar eventos');
      } finally {
        if (alive) setLoadingEv(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const weekEnd = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 6);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [today]);

  const isToday = (d) => normalizeDate(d).getTime() === today.getTime();

  const weekSpecialEvents = useMemo(() => {
    if (!events?.length) return [];
    const wStart = normalizeDate(today);
    const wEnd = normalizeDate(weekEnd);

    return events
      .filter((e) => e?.date)
      .map((e) => {
        const start = normalizeDate(e.date);
        const end = e.end_date ? normalizeDate(e.end_date) : new Date(start);
        if (end < start) end.setTime(start.getTime());
        return { ...e, _start: start, _end: end };
      })
      .filter((e) => e._start <= wEnd && e._end >= wStart)
      .sort((a, b) => a._start - b._start)
      .map((e) => ({
        id: `single-${e.id ?? e.title}`,
        kind: 'single',
        title: e.title,
        date: e.date,
        end_date: e.end_date,
        time: e.time,
        place: e.place,
        description: e.description,
        image: e.image,
        colorScheme: 'accent',
      }));
  }, [events, today, weekEnd]);

  const weekRecurringEvents = useMemo(() => {
    if (!fixedEvents?.length) return [];
    const actives = fixedEvents.filter((f) => f.isActive !== false);
    const out = [];

    for (const fe of actives) {
      for (let offset = 0; offset <= 6; offset++) {
        const d = new Date(today);
        d.setDate(today.getDate() + offset);
        d.setHours(0, 0, 0, 0);

        if (d > weekEnd) continue;
        if (!shouldRenderFixedEventOnDate(fe, d)) continue;

        out.push({
          id: `fixed-${fe.id}-${d.toISOString().slice(0, 10)}`,
          kind: 'recurring',
          title: fe.title,
          date: d.toISOString(),
          time: fe.time,
          place: fe.place,
          description: fe.description,
          image: fe.image,
          colorScheme: fe.color || 'blue',
        });
      }
    }
    return out.sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [fixedEvents, today, weekEnd]);

  const weekEvents = useMemo(() => {
    const all = [...weekSpecialEvents, ...weekRecurringEvents];
    return all
      .filter((ev) => ev.kind !== 'recurring')
      .sort((a, b) => {
        const da = new Date(a.date);
        const db = new Date(b.date);
        if (da.getTime() !== db.getTime()) return da - db;
        const ta = a.time || '';
        const tb = b.time || '';
        return ta.localeCompare(tb);
      });
  }, [weekSpecialEvents, weekRecurringEvents]);

  const openDetails = (ev) => {
    setCurrentEvent(ev);
    onOpen();
  };

  const quickLinks = [
    { label: 'Estudios Bíblicos', icon: FiBookOpen, to: '/estudio-biblico', color: 'blue.500' },
    { label: 'Devocionales', icon: FiHeart, to: '/devocionales', color: 'red.400' },
    { label: 'Nuestro Horario', icon: FiCalendar, to: '/nuestro-horario', color: 'green.500' },
    { label: 'Ubicación', icon: FiMap, to: '/contacto', color: 'orange.400' },
  ];

  return (
    <PageTransition>
      <Box display="flex" flexDirection="column" bg="bg.default" pb={12} minH="100vh">

        {/* HERO SECTION (Glassmorphism) */}
        <Box
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          minH={{ base: '60vh', md: '80vh' }}
          overflow="hidden"
          borderBottomRadius={{ base: '2xl', md: '3xl' }}
          boxShadow="lg"
        >
          <Box
            position="absolute"
            inset="0"
            bgImage="url('/images/generales/iglesia.jpg')"
            bgSize="cover"
            bgPosition="center"
            filter="brightness(60%)"
            zIndex={0}
          />

          <Container maxW="container.lg" position="relative" zIndex={1} px={4}>
            <MotionBox
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              bg={glassBg}
              backdropFilter="blur(8px)"
              p={{ base: 5, md: 10 }}
              my={{ base: 10, md: 0 }}
              borderRadius="2xl"
              boxShadow="xl"
              border="1px solid"
              borderColor="whiteAlpha.300"
              textAlign="center"
            >
              <Heading
                as="h1"
                size={{ base: 'lg', md: '2xl' }}
                color="text.default"
                mb={4}
                letterSpacing="tight"
              >
                Sala Evangélica, Barrio Lempira
              </Heading>
              <Text fontSize={{ base: 'sm', md: 'xl' }} color="text.default" mb={8} fontWeight="medium">
                Bienvenidos a nuestro espacio de fe y esperanza.
              </Text>

              <Box maxW="3xl" mx="auto" borderTop="1px solid" borderColor="border.default" pt={6}>
                <Heading as="h3" size="sm" color={useColorModeValue("brand.800", "accent.500")} textTransform="uppercase" letterSpacing="wider" mb={3}>
                  Versículo del Día
                </Heading>
                {daily ? (
                  <>
                    <Box
                      fontSize={{ base: 'md', md: '2xl' }}
                      color="text.default"
                      fontStyle="italic"
                      mb={3}
                      dangerouslySetInnerHTML={{
                        __html: safeHtmlAllowSup(daily.text || ''),
                      }}
                    />
                    <Text fontWeight="bold" color="text.default" fontSize={{ base: 'sm', md: 'md' }}>
                      — {daily.reference}
                    </Text>
                  </>
                ) : vErr ? (
                  <Text color="red.400">No se pudo cargar el versículo.</Text>
                ) : (
                  <Spinner color="accent.500" />
                )}
                {daily && (
                  <Box mt={4}>
                    <Button
                      size="sm"
                      colorScheme="accent"
                      variant="outline"
                      leftIcon={<FiShare2 />}
                      onClick={() => setIsShareModalOpen(true)}
                      bg={shareBtnBg}
                    >
                      Compartir Versículo
                    </Button>
                  </Box>
                )}
              </Box>

              <ShareCardModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                text={daily?.text}
                reference={daily?.reference}
              />


              {/* ANUNCIO NUEVO CONTENIDO */}
              <Box mt={6} p={4} bg={useColorModeValue('blue.50', 'blue.900')} borderRadius="xl" border="1px solid" borderColor={useColorModeValue('blue.200', 'blue.700')} _hover={{ transform: 'translateY(-2px)', shadow: 'md' }} transition="all 0.2s">
                <HStack justify="center" spacing={3}>
                  <Icon as={FiBookOpen} color={useColorModeValue('blue.600', 'blue.200')} w={5} h={5} />
                  <Text color={useColorModeValue('blue.800', 'blue.100')} fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>
                    ¡Nuevos estudios bíblicos y devocionales disponibles!
                  </Text>
                </HStack>
              </Box>
            </MotionBox>
          </Container>
        </Box>

        {/* QUICK LINKS SECTION */}
        <Container maxW="container.xl" mt={{ base: 4, md: -12 }} position="relative" zIndex={2}>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 4, md: 6 }}>
            {quickLinks.map((link, idx) => (
              <MotionBox
                key={idx}
                as={RouterLink}
                to={link.to}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                whileHover={{ y: -5, boxShadow: 'xl' }}
                bg={cardBg}
                p={{ base: 3, md: 6 }}
                borderRadius="xl"
                boxShadow="md"
                border="1px solid"
                borderColor="border.default"
                textAlign="center"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                _hover={{ textDecoration: 'none', borderColor: link.color }}
              >
                <Icon as={link.icon} w={8} h={8} color={link.color} mb={3} />
                <Text fontWeight="semibold" color="text.default">
                  {link.label}
                </Text>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>

        {/* EVENTOS SEMANALES */}
        <Container maxW="container.xl" py={{ base: 12, md: 16 }}>
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
          >
            <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
              <Heading size={{ base: 'lg', md: 'xl' }} color="text.default">
                Eventos de esta semana
              </Heading>
              <Button
                as={RouterLink}
                to="/nuestro-horario"
                size="md"
                colorScheme="accent"
                variant="outline"
                _hover={{ bg: 'accent.500', color: 'white' }}
              >
                Ver horario general
              </Button>
            </Flex>

            {loadingEv ? (
              <Flex justify="center" align="center" py={12}>
                <Spinner size="xl" color="accent.500" />
              </Flex>
            ) : errEv ? (
              <Box bg={boxBg} p={6} borderRadius="xl" textAlign="center">
                <Text color="red.500">No se pudieron cargar los eventos de esta semana.</Text>
              </Box>
            ) : weekEvents.length === 0 ? (
              <Box bg={boxBg} p={10} borderRadius="xl" textAlign="center" border="1px dashed" borderColor="border.default">
                <Icon as={FiCalendar} w={10} h={10} color="gray.400" mb={3} />
                <Text color="text.muted" fontSize="lg">
                  No hay eventos especiales programados para esta semana.
                </Text>
              </Box>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {weekEvents.map((ev, idx) => {
                  const evDate = new Date(ev.date);
                  const isTodayFlag = isToday(ev.date);

                  return (
                    <MotionBox
                      key={ev.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: idx * 0.1 }}
                      whileHover={{ y: -5 }}
                      bg={cardBg}
                      border="1px solid"
                      borderColor="border.default"
                      borderRadius="xl"
                      overflow="hidden"
                      boxShadow="sm"
                      display="flex"
                      flexDirection="column"
                    >
                      {ev.image && (
                        <Image
                          src={cld(ev.image, { w: 600, h: 300, crop: 'fill' })}
                          alt={ev.title}
                          h="180px"
                          w="100%"
                          objectFit="cover"
                        />
                      )}
                      <Box p={5} flex="1" display="flex" flexDirection="column">
                        <HStack justify="space-between" mb={2}>
                          <HStack spacing={2}>
                            <Badge colorScheme="brand" variant="subtle" px={2} py={1} borderRadius="md">
                              {evDate.toLocaleDateString()}
                            </Badge>
                            {isTodayFlag && <Badge colorScheme="red">Hoy</Badge>}
                          </HStack>
                          {ev.time && (
                            <HStack spacing={1} color="text.muted" fontSize="sm">
                              <FiClock />
                              <Text>{ev.time}</Text>
                            </HStack>
                          )}
                        </HStack>

                        <Heading size="md" mb={2} color="text.default">
                          {ev.title}
                        </Heading>

                        {ev.place && (
                          <HStack spacing={1} color="text.muted" fontSize="sm" mb={3}>
                            <FiMapPin />
                            <Text>{ev.place}</Text>
                          </HStack>
                        )}

                        <Text fontSize="sm" color="text.muted" noOfLines={3} mb={4} flex="1">
                          {ev.description}
                        </Text>

                        <Button size="sm" alignSelf="flex-start" colorScheme="accent" variant="ghost" onClick={() => openDetails(ev)}>
                          Ver detalles
                        </Button>
                      </Box>
                    </MotionBox>
                  );
                })}
              </SimpleGrid>
            )}
          </MotionBox>
        </Container>

        {/* QUIENES SOMOS SECTION */}
        <Container maxW="container.xl" pb={12}>
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            bg={cardBg}
            borderRadius="3xl"
            overflow="hidden"
            boxShadow="lg"
            border="1px solid"
            borderColor="border.default"
          >
            <Flex direction={{ base: 'column', md: 'row' }}>
              <Box flex="1" p={{ base: 8, md: 12 }} display="flex" flexDirection="column" justifyContent="center">
                <Badge colorScheme="accent" alignSelf="flex-start" mb={4} px={3} py={1} borderRadius="full">
                  Conócenos
                </Badge>
                <Heading size="xl" mb={4} color="text.default">
                  Nuestra Comunidad
                </Heading>
                <Text fontSize="lg" color="text.muted" mb={8} lineHeight="tall">
                  Somos una comunidad de fe que busca crecer en el conocimiento de Dios y servir a nuestro prójimo.
                  Te invitamos a ser parte de nuestras actividades, estudios y reuniones. ¡Hay un lugar para ti!
                </Text>
                <Button
                  as={RouterLink}
                  to="/ministerio"
                  size="lg"
                  colorScheme="accent"
                  alignSelf="flex-start"
                  px={8}
                >
                  Nuestros Líderes
                </Button>
              </Box>
              <Box flex="1" minH={{ base: '300px', md: 'auto' }} position="relative">
                <Image
                  src="/images/generales/iglesia.jpg"
                  alt="Comunidad"
                  objectFit="cover"
                  w="100%"
                  h="100%"
                  position="absolute"
                />
              </Box>
            </Flex>
          </MotionBox>
        </Container>

        {/* MODAL EVENTO */}
        {currentEvent && (
          <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'xl' }} isCentered scrollBehavior="inside">
            <ModalOverlay backdropFilter="blur(5px)" />
            <ModalContent bg="bg.card" color="text.default" borderRadius="2xl">
              <ModalHeader>{currentEvent.title}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack align="start" spacing={4}>
                  {currentEvent.image && (
                    <Image
                      src={cld(currentEvent.image, {
                        w: 1200,
                        h: 700,
                        crop: 'fill',
                        gravity: 'auto',
                      })}
                      alt={currentEvent.title}
                      w="100%"
                      h="220px"
                      objectFit="cover"
                      borderRadius="xl"
                    />
                  )}
                  <HStack spacing={3} color="text.muted" fontWeight="medium" flexWrap="wrap">
                    <Text>
                      {new Date(currentEvent.date).toLocaleDateString()}
                    </Text>
                    {currentEvent.time && (
                      <HStack spacing={1}>
                        <FiClock />
                        <Text>{currentEvent.time}</Text>
                      </HStack>
                    )}
                    {currentEvent.place && (
                      <HStack spacing={1}>
                        <FiMapPin />
                        <Text>{currentEvent.place}</Text>
                      </HStack>
                    )}
                    {currentEvent.kind === 'recurring' && (
                      <Badge colorScheme={currentEvent.colorScheme || 'accent'} borderRadius="md">
                        Evento recurrente
                      </Badge>
                    )}
                  </HStack>
                  {currentEvent.description && (
                    <Text color="text.muted" fontSize="md" lineHeight="relaxed">{currentEvent.description}</Text>
                  )}
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button onClick={onClose} variant="ghost">Cerrar</Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
      </Box>
    </PageTransition>
  );
}
